import { useState, useMemo } from 'react';
import { ITimelineEvent } from '@/types/timeline';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'debug-time-machine-ui';
import { 
  ChevronDown, 
  ChevronRight, 
  Copy, 
  Star, 
  Clock,
  Info,
  AlertTriangle,
  Bug,
  Activity,
  Database,
  Eye,
  Code,
  Bookmark
} from 'lucide-react';

interface StateInspectorProps {
  selectedEvent: ITimelineEvent | null;
  currentTime: number;
  toggleBookmark?: (eventId: string) => void;
}

interface StateProperty {
  key: string;
  value: any;
  type: string;
  changed?: boolean;
}

const StateInspector = ({ selectedEvent, currentTime, toggleBookmark }: StateInspectorProps) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const copyToClipboard = async (text: string, path?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPath(path || 'content');
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (error) {
      console.warn('Failed to copy to clipboard:', error);
    }
  };

  const formatValue = (value: any, depth = 0): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      if (depth > 2) return `Array(${value.length})`;
      return `[${value.map(v => formatValue(v, depth + 1)).join(', ')}]`;
    }
    if (typeof value === 'object') {
      if (depth > 2) return `Object(${Object.keys(value).length} keys)`;
      const entries = Object.entries(value).slice(0, 3);
      const display = entries.map(([k, v]) => `${k}: ${formatValue(v, depth + 1)}`).join(', ');
      return `{${display}${Object.keys(value).length > 3 ? '...' : ''}}`;
    }
    return String(value);
  };

  const getValueType = (value: any): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  };

  const extractStateProperties = (data: any): StateProperty[] => {
    if (!data || typeof data !== 'object') return [];

    const properties: StateProperty[] = [];

    // 상태 관련 데이터 추출
    if (data.newState) {
      Object.entries(data.newState).forEach(([key, value]) => {
        properties.push({
          key,
          value,
          type: getValueType(value),
          changed: data.prevState && data.prevState[key] !== value
        });
      });
    }

    // props 추출
    if (data.props) {
      Object.entries(data.props).forEach(([key, value]) => {
        properties.push({
          key: `props.${key}`,
          value,
          type: getValueType(value),
        });
      });
    }

    // API 응답 데이터 추출
    if (data.responseData) {
      Object.entries(data.responseData).forEach(([key, value]) => {
        properties.push({
          key: `response.${key}`,
          value,
          type: getValueType(value),
        });
      });
    }

    // 기타 데이터 추출
    const excludeKeys = new Set(['newState', 'prevState', 'props', 'responseData', 'componentName', 'target']);
    Object.entries(data).forEach(([key, value]) => {
      if (!excludeKeys.has(key)) {
        properties.push({
          key,
          value,
          type: getValueType(value),
        });
      }
    });

    return properties;
  };

  const stateProperties = useMemo(() => {
    if (!selectedEvent?.data) return [];
    return extractStateProperties(selectedEvent.data);
  }, [selectedEvent]);

  const getEventIcon = (event: ITimelineEvent) => {
    if (event.isError || event.severity === 'critical') {
      return <Bug className="w-5 h-5 text-red-500" />;
    }
    if (event.severity === 'warning') {
      return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    }

    switch (event.type) {
      case 'state_change':
        return <Database className="w-5 h-5 text-emerald-500" />;
      case 'api_call':
        return <Activity className="w-5 h-5 text-blue-500" />;
      case 'user_action':
        return <Eye className="w-5 h-5 text-purple-500" />;
      case 'performance':
        return <Activity className="w-5 h-5 text-orange-500" />;
      default:
        return <Info className="w-5 h-5 text-slate-500" />;
    }
  };

  const PropertyValue = ({ property, path = '' }: { property: StateProperty; path?: string }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const fullPath = path ? `${path}.${property.key}` : property.key;
    const isComplex = typeof property.value === 'object' && property.value !== null;

    return (
      <div className="border-l-2 border-slate-100 dark:border-slate-700 pl-3 py-1">
        <div className="flex items-center justify-between group">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isComplex && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </Button>
            )}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className={`font-mono text-sm ${property.changed ? 'text-amber-600 font-semibold' : 'text-slate-600 dark:text-slate-400'}`}>
                {property.key}:
              </span>
              <Badge variant="outline" className="text-xs">
                {property.type}
              </Badge>
              {property.changed && (
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                  changed
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
              onClick={() => copyToClipboard(JSON.stringify(property.value, null, 2), fullPath)}
            >
              <Copy className="w-3 h-3" />
            </Button>
            {copiedPath === fullPath && (
              <span className="text-xs text-emerald-600">Copied!</span>
            )}
          </div>
        </div>
        
        <div className="mt-1">
          {!isComplex ? (
            <code className="text-xs bg-slate-100 dark:bg-slate-800 rounded px-2 py-1 block break-all">
              {formatValue(property.value)}
            </code>
          ) : (
            <>
              <code className="text-xs text-slate-500 dark:text-slate-400">
                {formatValue(property.value, 0)}
              </code>
              {isExpanded && (
                <div className="mt-2 ml-4">
                  <pre className="text-xs bg-slate-100 dark:bg-slate-800 rounded p-2 overflow-auto max-h-40">
                    {JSON.stringify(property.value, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  if (!selectedEvent) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-slate-500 dark:text-slate-400">
          <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">No Event Selected</p>
          <p className="text-sm">Select an event from the timeline to inspect its state</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Event Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {getEventIcon(selectedEvent)}
              <div>
                <CardTitle className="text-base leading-tight">{selectedEvent.title}</CardTitle>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {new Date(selectedEvent.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {selectedEvent.severity && (
                <Badge 
                  variant={
                    selectedEvent.severity === 'critical' ? 'destructive' :
                    selectedEvent.severity === 'warning' ? 'outline' : 'secondary'
                  }
                >
                  {selectedEvent.severity}
                </Badge>
              )}
              {toggleBookmark && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleBookmark(selectedEvent.id)}
                  className={selectedEvent.isBookmarked ? 'text-amber-500' : ''}
                >
                  {selectedEvent.isBookmarked ? <Star className="w-4 h-4 fill-current" /> : <Bookmark className="w-4 h-4" />}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {selectedEvent.description}
          </p>
          {selectedEvent.suggestion && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                💡 {selectedEvent.suggestion}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* State Data */}
      <div className="flex-1 overflow-hidden">
        <Tabs defaultValue="state" className="h-full flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="state">State</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="raw">Raw Data</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-hidden mt-4">
            <TabsContent value="state" className="h-full overflow-y-auto mt-0">
              {stateProperties.length > 0 ? (
                <div className="space-y-3">
                  {stateProperties.map((property, index) => (
                    <PropertyValue key={index} property={property} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No state data available</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="details" className="h-full overflow-y-auto mt-0">
              <div className="space-y-4">
                {/* Event Metadata */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Event Metadata</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">ID</p>
                        <p className="font-mono">{selectedEvent.id}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Type</p>
                        <p className="font-mono">{selectedEvent.type}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Timestamp</p>
                        <p className="font-mono">{selectedEvent.timestamp}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Client ID</p>
                        <p className="font-mono">{selectedEvent.clientId || 'N/A'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Component Info */}
                {selectedEvent.data?.componentName && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Component Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Component Name</p>
                          <p className="font-mono">{selectedEvent.data.componentName}</p>
                        </div>
                        {selectedEvent.data.target && (
                          <div>
                            <p className="text-slate-500 dark:text-slate-400">Target Element</p>
                            <p className="font-mono">{selectedEvent.data.target.tagName}</p>
                            {selectedEvent.data.target.id && (
                              <p className="font-mono text-xs text-slate-400">#{selectedEvent.data.target.id}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* API Details */}
                {selectedEvent.type === 'api_call' && selectedEvent.data && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">API Call Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Method</p>
                        <Badge variant="outline">{selectedEvent.data.method || 'GET'}</Badge>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">URL</p>
                        <p className="font-mono text-xs break-all">{selectedEvent.data.url}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Status</p>
                          <Badge variant={selectedEvent.data.status >= 400 ? 'destructive' : 'secondary'}>
                            {selectedEvent.data.status}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Duration</p>
                          <p className="font-mono">{selectedEvent.data.duration}ms</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Memory Info */}
                {(selectedEvent.memorySize || selectedEvent.isCompressed) && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Memory Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {selectedEvent.memorySize && (
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Memory Size</p>
                          <p className="font-mono">{(selectedEvent.memorySize / 1024).toFixed(1)} KB</p>
                        </div>
                      )}
                      {selectedEvent.isCompressed && (
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Compression</p>
                          <Badge variant="outline">Compressed</Badge>
                          {selectedEvent.originalDataSize && (
                            <p className="text-xs text-slate-400 mt-1">
                              Original: {(selectedEvent.originalDataSize / 1024).toFixed(1)} KB
                            </p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="raw" className="h-full overflow-y-auto mt-0">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Raw Event Data</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(JSON.stringify(selectedEvent, null, 2))}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy JSON
                  </Button>
                </CardHeader>
                <CardContent>
                  <pre className="text-xs bg-slate-100 dark:bg-slate-800 rounded p-4 overflow-auto max-h-96 font-mono">
                    {JSON.stringify(selectedEvent, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default StateInspector;
